import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useClickOutside from '../../../../../hooks/useClickOutside';
import { COUNTRIES, useCountryName } from '../../../../../utils/countries';
import { useTranslation } from 'react-i18next';

interface CountryDropdownProps {
  countryCode: string;
  onSelect: (code: string) => void;
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({ countryCode, onSelect }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  // Usando o hook para obter o nome traduzido do país selecionado
  const currentCountryName = useCountryName(countryCode);

  const handleSelect = (code: string) => {
    onSelect(code);
    setIsOpen(false);
  };

  // Função auxiliar para obter o nome traduzido de uma opção de país
  const getTranslatedCountryName = (code: string) => {
    const key = `countries_list.${code.toLowerCase()}`;
    return t(key, { defaultValue: code.toUpperCase() });
  };

  return (
    <div className="relative w-full md:w-auto" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-14 w-full shrink-0 items-center justify-between gap-x-2 rounded-lg bg-gray-100 dark:bg-background-dark-elevated2 pl-4 pr-2 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors border border-transparent dark:border-primary-darkMode"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">location_on</span>
          <p className="text-[#181411] dark:text-white text-base font-normal leading-normal">
            {currentCountryName}
          </p>
        </div>
        <motion.span
          className="material-symbols-outlined text-lg"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          expand_more
        </motion.span>
      </motion.button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute top-full left-0 mt-2 w-full max-w-xs bg-white dark:bg-background-dark-elevated rounded-lg shadow-lg border border-gray-200 dark:border-primary-darkMode z-50 max-h-60 overflow-y-auto"
        >
          <div className="p-2">
            {COUNTRIES.map((country, index) => (
              <motion.button
                key={country.code}
                onClick={() => handleSelect(country.code)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex justify-between items-center ${countryCode === country.code
                  ? 'bg-primary text-white'
                  : 'text-gray-700 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-background-dark-elevated3'
                  }`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                whileHover={{ x: 5 }}
              >
                <span>{getTranslatedCountryName(country.code)}</span>
                <span className="text-xs font-semibold opacity-70">{country.code.toUpperCase()}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CountryDropdown;